import React, { useEffect, useState, useCallback } from 'react';
import { usePerformanceMonitor } from '../hooks/usePerformanceMonitor';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';

interface PerformanceOptimizerProps {
  children: React.ReactNode;
  enableAutoOptimization?: boolean;
  showPerformanceIndicator?: boolean;
}

interface OptimizationState {
  reducedAnimations: boolean;
  lowQualityMode: boolean;
  reducedParticles: boolean;
  disableTransitions: boolean;
}

const PerformanceOptimizer: React.FC<PerformanceOptimizerProps> = ({
  children,
  enableAutoOptimization = true,
  showPerformanceIndicator = false
}) => {
  const [optimizations, setOptimizations] = useState<OptimizationState>({
    reducedAnimations: false,
    lowQualityMode: false,
    reducedParticles: false,
    disableTransitions: false
  });

  const { performanceRecommendations, isMobile } = useResponsiveLayout();
  
  const { metrics, getDeviceCapabilities } = usePerformanceMonitor({
    enableFPSMonitoring: enableAutoOptimization,
    enableMemoryMonitoring: enableAutoOptimization,
    fpsThreshold: 30,
    memoryThreshold: 100,
    onPerformanceChange: useCallback((newMetrics) => {
      if (!enableAutoOptimization) return;

      setOptimizations(prev => {
        const newOptimizations = { ...prev };
        
        // Auto-optimize based on FPS
        if (newMetrics.fps < 20) {
          newOptimizations.reducedAnimations = true;
          newOptimizations.lowQualityMode = true;
          newOptimizations.reducedParticles = true;
          newOptimizations.disableTransitions = true;
        } else if (newMetrics.fps < 30) {
          newOptimizations.reducedAnimations = true;
          newOptimizations.reducedParticles = true;
        } else if (newMetrics.fps > 50) {
          // Performance is good, can enable features
          newOptimizations.reducedAnimations = false;
          newOptimizations.disableTransitions = false;
        }

        // Auto-optimize based on memory usage
        if (newMetrics.memoryUsage && newMetrics.memoryUsage > 150) {
          newOptimizations.lowQualityMode = true;
          newOptimizations.reducedParticles = true;
        }

        return newOptimizations;
      });
    }, [enableAutoOptimization])
  });

  // Apply device-based optimizations on mount
  useEffect(() => {
    const deviceCapabilities = getDeviceCapabilities();
    
    setOptimizations(prev => ({
      ...prev,
      reducedAnimations: performanceRecommendations.reduceAnimations || deviceCapabilities.isLowEnd,
      lowQualityMode: deviceCapabilities.isLowEnd || isMobile,
      reducedParticles: performanceRecommendations.limitParticles || deviceCapabilities.isLowEnd,
      disableTransitions: deviceCapabilities.isLowEnd && isMobile
    }));
  }, [performanceRecommendations, isMobile, getDeviceCapabilities]);

  // Apply CSS optimizations
  useEffect(() => {
    const root = document.documentElement;
    
    if (optimizations.reducedAnimations) {
      root.style.setProperty('--animation-duration', '0.1s');
      root.style.setProperty('--transition-duration', '0.1s');
    } else {
      root.style.removeProperty('--animation-duration');
      root.style.removeProperty('--transition-duration');
    }

    if (optimizations.disableTransitions) {
      root.style.setProperty('--transition-duration', '0s');
    }

    if (optimizations.lowQualityMode) {
      root.style.setProperty('--blur-amount', '0px');
      root.style.setProperty('--shadow-intensity', '0.1');
    } else {
      root.style.removeProperty('--blur-amount');
      root.style.removeProperty('--shadow-intensity');
    }

    return () => {
      // Cleanup on unmount
      root.style.removeProperty('--animation-duration');
      root.style.removeProperty('--transition-duration');
      root.style.removeProperty('--blur-amount');
      root.style.removeProperty('--shadow-intensity');
    };
  }, [optimizations]);

  // Performance indicator component
  const PerformanceIndicator = () => {
    if (!showPerformanceIndicator || process.env.NODE_ENV !== 'development') {
      return null;
    }

    const getPerformanceColor = (fps: number) => {
      if (fps >= 50) return 'text-green-500';
      if (fps >= 30) return 'text-yellow-500';
      return 'text-red-500';
    };

    return null;
  };

  // Create optimized context
  const optimizedContext = {
    ...optimizations,
    metrics,
    isOptimized: Object.values(optimizations).some(Boolean)
  };

  return (
    <div 
      className={`performance-optimized ${optimizations.lowQualityMode ? 'low-quality' : ''}`}
      data-optimizations={JSON.stringify(optimizations)}
    >
      {React.cloneElement(children as React.ReactElement, {
        performanceContext: optimizedContext
      })}
      <PerformanceIndicator />
    </div>
  );
};

// Hook to access performance context in child components
export const usePerformanceContext = () => {
  const element = document.querySelector('[data-optimizations]');
  if (!element) return null;
  
  try {
    return JSON.parse(element.getAttribute('data-optimizations') || '{}');
  } catch {
    return null;
  }
};

// Higher-order component for performance optimization
export const withPerformanceOptimization = <P extends object>(
  Component: React.ComponentType<P>,
  options: { enableAutoOptimization?: boolean; showIndicator?: boolean } = {}
) => {
  return React.forwardRef<any, P>((props, ref) => (
    <PerformanceOptimizer 
      enableAutoOptimization={options.enableAutoOptimization}
      showPerformanceIndicator={options.showIndicator}
    >
      <Component {...props} ref={ref} />
    </PerformanceOptimizer>
  ));
};

export default PerformanceOptimizer;